import { apiClient } from './client';
import type {
  CreateMargConfigDto,
  CreateMargGlMappingRuleDto,
  ForceUnlockResponse,
  LockedConfigInfo,
  MargGlAccount,
  MargGlMappingRule,
  MargReconciliationResult,
  MargReconciliationStatus,
  MargReconciliationType,
  MargStagedAccountGroup,
  MargStagedAccountGroupBalance,
  MargStagedAccountPosting,
  MargStagedBranch,
  MargStagedCompany,
  MargStagedFilter,
  MargStagedOutstanding,
  MargStagedParty,
  MargStagedPartyBalance,
  MargStagedProduct,
  MargStagedStock,
  MargTransactionPage,
  MargTransactionPaging,
  MargSyncConfig,
  MargSyncLog,
  MargSyncOverview,
  PaginatedResponse,
  RecoverStaleSyncLogResponse,
  ResetSyncCursorResponse,
  ResumeSyncResponse,
  TestConnectionResponse,
  TriggerSyncParams,
  TriggerSyncResponse,
  UpdateMargConfigDto,
  UpdateMargGlMappingRuleDto,
} from './marg-ede.types';

export type {
  MargSyncFrequency,
  MargSyncStatus,
  MargSyncScope,
  MargReconciliationType,
  MargReconciliationStatus,
  MargSyncLog,
  MargSyncConfig,
  CreateMargConfigDto,
  UpdateMargConfigDto,
  MargSyncOverviewConfig,
  MargSyncOverview,
  PaginatedResponse,
  MargStagedBranch,
  MargStagedProduct,
  MargStagedParty,
  MargStagedTransaction,
  MargTransactionPage,
  MargTransactionPaging,
  MargStagedStock,
  MargStagedAccountPosting,
  MargStagedAccountGroup,
  MargStagedAccountGroupBalance,
  MargStagedPartyBalance,
  MargStagedOutstanding,
  MargStagedCompany,
  MargStagedFilter,
  MargStagedOrigin,
  MargStagedSource,
  MargGlAccount,
  MargGlMappingRule,
  CreateMargGlMappingRuleDto,
  UpdateMargGlMappingRuleDto,
  MargReconciliationResult,
  TriggerSyncParams,
  TriggerSyncResponse,
  ResumeSyncResponse,
  ResetSyncCursorResponse,
  ForceUnlockResponse,
  RecoverStaleSyncLogResponse,
  LockedConfigInfo,
  MargConnectionProbeSummary,
  TestConnectionResponse,
} from './marg-ede.types';

const BASE_PATH = '/marg-ede';

/** A page of staged rows, optionally of one source or company. */
type StagedParams = { page?: number; pageSize?: number } & MargStagedFilter;

export const margEdeService = {
  async getConfigs(): Promise<MargSyncConfig[]> {
    const { data } = await apiClient.get<MargSyncConfig[]>(`${BASE_PATH}/configs`);
    return data;
  },

  async getConfig(configId: string): Promise<MargSyncConfig> {
    const { data } = await apiClient.get<MargSyncConfig>(`${BASE_PATH}/configs/${configId}`);
    return data;
  },

  async createConfig(dto: CreateMargConfigDto): Promise<MargSyncConfig> {
    const { data } = await apiClient.post<MargSyncConfig>(`${BASE_PATH}/configs`, dto);
    return data;
  },

  async updateConfig(configId: string, dto: UpdateMargConfigDto): Promise<MargSyncConfig> {
    const { data } = await apiClient.patch<MargSyncConfig>(`${BASE_PATH}/configs/${configId}`, dto);
    return data;
  },

  async deleteConfig(configId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/configs/${configId}`);
  },

  async testConnection(configId: string): Promise<TestConnectionResponse> {
    const { data } = await apiClient.post<TestConnectionResponse>(`${BASE_PATH}/configs/${configId}/test`);
    return data;
  },

  async triggerSync(configId: string, params?: TriggerSyncParams): Promise<TriggerSyncResponse> {
    const { data } = await apiClient.post<TriggerSyncResponse>(`${BASE_PATH}/configs/${configId}/sync`, undefined, {
      params,
    });
    return data;
  },

  async triggerAccountingSync(configId: string, params?: TriggerSyncParams): Promise<TriggerSyncResponse> {
    const { data } = await apiClient.post<TriggerSyncResponse>(
      `${BASE_PATH}/configs/${configId}/sync/accounting`,
      undefined,
      {
        params,
      },
    );
    return data;
  },

  async getSyncLogs(configId: string): Promise<MargSyncLog[]> {
    const { data } = await apiClient.get<MargSyncLog[]>(`${BASE_PATH}/configs/${configId}/logs`);
    return data;
  },

  async getOverview(): Promise<MargSyncOverview> {
    const { data } = await apiClient.get<MargSyncOverview>(`${BASE_PATH}/overview`);
    return data;
  },

  async getGlAccounts(): Promise<MargGlAccount[]> {
    const { data } = await apiClient.get<MargGlAccount[]>(`${BASE_PATH}/gl-accounts`);
    return data;
  },

  async getGlMappingRules(params?: { companyId?: number; isActive?: boolean }): Promise<MargGlMappingRule[]> {
    const { data } = await apiClient.get<MargGlMappingRule[]>(`${BASE_PATH}/gl-mapping-rules`, { params });
    return data;
  },

  async createGlMappingRule(dto: CreateMargGlMappingRuleDto): Promise<MargGlMappingRule> {
    const { data } = await apiClient.post<MargGlMappingRule>(`${BASE_PATH}/gl-mapping-rules`, dto);
    return data;
  },

  async updateGlMappingRule(ruleId: string, dto: UpdateMargGlMappingRuleDto): Promise<MargGlMappingRule> {
    const { data } = await apiClient.patch<MargGlMappingRule>(`${BASE_PATH}/gl-mapping-rules/${ruleId}`, dto);
    return data;
  },

  async deleteGlMappingRule(ruleId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/gl-mapping-rules/${ruleId}`);
  },

  async getReconciliationResults(params?: {
    configId?: string;
    syncLogId?: string;
    type?: MargReconciliationType;
    status?: MargReconciliationStatus;
    take?: number;
  }): Promise<MargReconciliationResult[]> {
    const { data } = await apiClient.get<MargReconciliationResult[]>(`${BASE_PATH}/reconciliation-results`, { params });
    return data;
  },

  async getStagedCompanies(): Promise<MargStagedCompany[]> {
    const { data } = await apiClient.get<MargStagedCompany[]>(`${BASE_PATH}/staged/companies`);
    return data;
  },

  async getStagedBranches(params?: StagedParams): Promise<PaginatedResponse<MargStagedBranch>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedBranch>>(`${BASE_PATH}/staged/branches`, {
      params,
    });
    return data;
  },

  async getStagedProducts(params?: StagedParams): Promise<PaginatedResponse<MargStagedProduct>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedProduct>>(`${BASE_PATH}/staged/products`, {
      params,
    });
    return data;
  },

  async getStagedParties(params?: StagedParams): Promise<PaginatedResponse<MargStagedParty>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedParty>>(`${BASE_PATH}/staged/parties`, { params });
    return data;
  },

  async getStagedTransactions(params?: StagedParams & MargTransactionPaging): Promise<MargTransactionPage> {
    const { data } = await apiClient.get<MargTransactionPage>(`${BASE_PATH}/staged/transactions`, {
      params,
    });
    return data;
  },

  async getStagedStock(params?: StagedParams): Promise<PaginatedResponse<MargStagedStock>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedStock>>(`${BASE_PATH}/staged/stock`, { params });
    return data;
  },

  async getStagedAccountPostings(params?: StagedParams): Promise<PaginatedResponse<MargStagedAccountPosting>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedAccountPosting>>(
      `${BASE_PATH}/staged/account-postings`,
      { params },
    );
    return data;
  },

  async getStagedAccountGroups(params?: StagedParams): Promise<PaginatedResponse<MargStagedAccountGroup>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedAccountGroup>>(
      `${BASE_PATH}/staged/account-groups`,
      { params },
    );
    return data;
  },

  async getStagedAccountGroupBalances(
    params?: StagedParams,
  ): Promise<PaginatedResponse<MargStagedAccountGroupBalance>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedAccountGroupBalance>>(
      `${BASE_PATH}/staged/account-group-balances`,
      { params },
    );
    return data;
  },

  async getStagedPartyBalances(params?: StagedParams): Promise<PaginatedResponse<MargStagedPartyBalance>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedPartyBalance>>(
      `${BASE_PATH}/staged/party-balances`,
      { params },
    );
    return data;
  },

  async getStagedOutstandings(params?: StagedParams): Promise<PaginatedResponse<MargStagedOutstanding>> {
    const { data } = await apiClient.get<PaginatedResponse<MargStagedOutstanding>>(`${BASE_PATH}/staged/outstandings`, {
      params,
    });
    return data;
  },

  // ==================== RECOVERY / OPERATIONAL ====================

  async resumeSync(configId: string, syncLogId: string): Promise<ResumeSyncResponse> {
    const { data } = await apiClient.post<ResumeSyncResponse>(
      `${BASE_PATH}/configs/${configId}/syncs/${syncLogId}/resume`,
    );
    return data;
  },

  async resetSyncCursor(
    configId: string,
    options?: { scope?: 'FULL' | 'ACCOUNTING'; clearStaging?: boolean },
  ): Promise<ResetSyncCursorResponse> {
    const { data } = await apiClient.post<ResetSyncCursorResponse>(
      `${BASE_PATH}/configs/${configId}/reset-cursor`,
      undefined,
      { params: options },
    );
    return data;
  },

  async forceUnlock(configId: string, force = false): Promise<ForceUnlockResponse> {
    const { data } = await apiClient.post<ForceUnlockResponse>(
      `${BASE_PATH}/configs/${configId}/force-unlock`,
      undefined,
      { params: { force } },
    );
    return data;
  },

  async recoverStaleSyncLog(configId: string, syncLogId: string): Promise<RecoverStaleSyncLogResponse> {
    const { data } = await apiClient.post<RecoverStaleSyncLogResponse>(
      `${BASE_PATH}/configs/${configId}/syncs/${syncLogId}/recover-stale`,
    );
    return data;
  },

  async getLockedConfigs(): Promise<LockedConfigInfo[]> {
    const { data } = await apiClient.get<LockedConfigInfo[]>(`${BASE_PATH}/configs/locked`);
    return data;
  },
};

export default margEdeService;
